import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Approval, approveRequest, listApprovals, rejectRequest } from "../api/approvals.ts";

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: "text-yellow-400 bg-yellow-900/30",
  UNDER_REVIEW: "text-blue-400 bg-blue-900/30",
  APPROVED: "text-green-400 bg-green-900/30",
  REJECTED: "text-red-400 bg-red-900/30",
  EXPIRED: "text-gray-500 bg-gray-800",
};

export function ApprovalsPage() {
  const { token } = useAuth();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  function load() {
    if (token == null) return;
    setLoading(true);
    listApprovals(token, { limit: 50 })
      .then((res) => setApprovals(res))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to load approvals"),
      )
      .finally(() => setLoading(false));
  }

  useEffect(load, [token]);

  async function handleApprove(id: string) {
    if (token == null) return;
    setActing(id);
    try {
      await approveRequest(token, id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve");
    } finally {
      setActing(null);
    }
  }

  async function handleReject(id: string) {
    if (token == null) return;
    setActing(id);
    try {
      await rejectRequest(token, id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject");
    } finally {
      setActing(null);
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Approvals</h1>

        {loading && <p className="text-gray-400">Loading...</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && approvals.length === 0 && (
          <p className="text-gray-500">No pending approvals.</p>
        )}

        <div className="space-y-3">
          {approvals.map((approval) => (
            <div
              key={approval.id}
              className="space-y-3 rounded-lg border border-gray-800 bg-gray-900 p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-white">{approval.title}</p>
                  <p className="mt-0.5 font-mono text-xs text-gray-500">{approval.approvalType}</p>
                </div>
                <span
                  className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[approval.status] ?? "bg-gray-800 text-gray-400"}`}
                >
                  {approval.status}
                </span>
              </div>

              {approval.description != null && (
                <p className="text-sm text-gray-400">{approval.description}</p>
              )}

              <div className="space-y-0.5 text-xs text-gray-500">
                {approval.requestedBy != null && <p>Requested by: {approval.requestedBy}</p>}
                {approval.expiresAt != null && (
                  <p>Expires: {new Date(approval.expiresAt).toLocaleString()}</p>
                )}
              </div>

              {(approval.status === "REQUESTED" || approval.status === "UNDER_REVIEW") && (
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => void handleApprove(approval.id)}
                    disabled={acting === approval.id}
                    className="rounded bg-green-700 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-600 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => void handleReject(approval.id)}
                    disabled={acting === approval.id}
                    className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              )}

              {approval.reviewNote != null && (
                <p className="border-t border-gray-800 pt-2 text-xs italic text-gray-400">
                  Note: {approval.reviewNote}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
